/**
 * KBFileDropzone — file dropzone for KB document upload.
 * Accepts PDF, TXT, HTML, and DOCX.
 * WCAG 2.1 AA: role=button, aria-label, aria-invalid, aria-describedby.
 */
import { useRef, useState, useCallback } from 'react';
import { UploadCloud, FileText, X, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const ACCEPTED_TYPES = [
  'application/pdf',
  'text/plain',
  'text/html',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_BYTES = 50 * 1024 * 1024;

interface KBFileDropzoneProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  error?: string | null;
  disabled?: boolean;
}

export function KBFileDropzone({ file, onFileChange, error, disabled }: KBFileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const displayError = error ?? validationError;

  const validate = useCallback((f: File): string | null => {
    if (!ACCEPTED_TYPES.includes(f.type)) {
      return 'Accepted file types: PDF, TXT, HTML, DOCX.';
    }
    if (f.size > MAX_BYTES) return 'File is too large. Maximum size is 50 MB.';
    return null;
  }, []);

  const handleFile = useCallback((f: File) => {
    const err = validate(f);
    setValidationError(err);
    onFileChange(err ? null : f);
  }, [validate, onFileChange]);

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files.item(0);
    if (dropped) handleFile(dropped);
  };

  const openPicker = () => { if (!disabled) inputRef.current?.click(); };

  return (
    <div className="space-y-1.5">
      <input
        ref={inputRef}
        id="kb-file-input"
        type="file"
        accept=".pdf,.txt,.html,.htm,.docx,application/pdf,text/plain,text/html,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="sr-only"
        aria-label="Select legislation file"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        disabled={disabled}
      />

      {file ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-3">
          <FileText className="h-6 w-6 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="flex-1 truncate text-sm text-foreground">{file.name}</span>
          {!disabled && (
            <button type="button" onClick={() => { setValidationError(null); onFileChange(null); }}
              className="rounded p-1 text-muted-foreground hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Remove selected file">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      ) : (
        <div
          role="button" tabIndex={disabled ? -1 : 0}
          aria-label="Drop zone: drag and drop a PDF, TXT, HTML, or DOCX file here, or press Enter to browse"
          aria-invalid={!!displayError}
          aria-describedby={displayError ? 'kb-file-error' : undefined}
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => { setIsDragging(false); }}
          onClick={openPicker}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPicker(); }}
          className={cn(
            'flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60',
            displayError && 'border-destructive',
            disabled && 'pointer-events-none opacity-50',
          )}
        >
          <UploadCloud className={cn('h-8 w-8', isDragging ? 'text-primary' : 'text-muted-foreground')} aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">Drop legislation document here, or click to browse</p>
          <p className="text-xs text-muted-foreground">Accepted: PDF, TXT, HTML, DOCX (max 50 MB)</p>
        </div>
      )}

      {displayError && (
        <p id="kb-file-error" role="alert" className="flex items-center gap-1.5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />{displayError}
        </p>
      )}
    </div>
  );
}
